import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';

/**
 * Row action menu for the admin tables.
 *
 * The tables live inside `.table-responsive`, whose `overflow-x: auto` clips
 * absolutely positioned children on both axes, so a menu opened on a short
 * table (a plate/violation search returning one row) was cut off — z-index
 * can't escape an overflow clip. The menu is therefore rendered in a portal
 * on document.body and positioned from the toggle's viewport rect, which no
 * ancestor can clip regardless of how many rows the table has.
 */

const MENU_OFFSET = 4;
const VIEWPORT_MARGIN = 8;
const DEFAULT_MENU_WIDTH = 180;

const RowActionsContext = createContext({ close: () => {} });

const RowActions = ({
  label = 'Actions',
  children,
  menuWidth = DEFAULT_MENU_WIDTH,
  toggleStyle = {},
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const place = useCallback(() => {
    if (!toggleRef.current) return;
    const rect = toggleRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current ? menuRef.current.offsetHeight : 0;
    // Flip above the toggle when the menu would run past the bottom of the
    // viewport and there is room above it.
    const flipUp =
      menuHeight > 0 &&
      rect.bottom + menuHeight + MENU_OFFSET > window.innerHeight - VIEWPORT_MARGIN &&
      rect.top - menuHeight - MENU_OFFSET > VIEWPORT_MARGIN;

    setPosition({
      top: flipUp ? rect.top - menuHeight - MENU_OFFSET : rect.bottom + MENU_OFFSET,
      // Right-aligned with the toggle, kept inside the viewport.
      left: Math.min(
        Math.max(VIEWPORT_MARGIN, rect.right - menuWidth),
        Math.max(VIEWPORT_MARGIN, window.innerWidth - menuWidth - VIEWPORT_MARGIN),
      ),
    });
  }, [menuWidth]);

  // Measure once the menu is in the DOM so the flip decision uses its height.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocumentMouseDown = event => {
      const insideMenu = menuRef.current && menuRef.current.contains(event.target);
      const insideToggle = toggleRef.current && toggleRef.current.contains(event.target);
      if (!insideMenu && !insideToggle) close();
    };
    const onKeyDown = event => {
      if (event.key === 'Escape') close();
    };
    // Capture phase so scrolling any ancestor container repositions the menu,
    // not just the window.
    const onScrollOrResize = () => place();

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, close, place]);

  const onToggleClick = event => {
    // Table rows navigate on click; never let the toggle bubble up to them.
    event.stopPropagation();
    event.preventDefault();
    setOpen(previous => !previous);
  };

  return (
    <div className={`row-actions d-inline-block ${className}`.trim()}>
      <button
        type="button"
        ref={toggleRef}
        className="btn btn-light"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={onToggleClick}
        style={{
          border: '1px solid #eaedf3',
          padding: '0px 12px',
          height: '28px',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          fontWeight: '500',
          ...toggleStyle,
        }}
      >
        {label}
        <FontAwesomeIcon icon={faCaretDown} className="ml-1" size="sm" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="dropdown-menu show"
            onClick={event => event.stopPropagation()}
            style={{
              position: 'fixed',
              top: position ? position.top : -9999,
              left: position ? position.left : -9999,
              // Hidden until measured so it never flashes in the wrong spot.
              visibility: position ? 'visible' : 'hidden',
              minWidth: menuWidth,
              margin: 0,
              boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
              zIndex: 1060,
            }}
          >
            <RowActionsContext.Provider value={{ close }}>{children}</RowActionsContext.Provider>
          </div>,
          document.body,
        )}
    </div>
  );
};

const Item = ({ children, onClick, className = '', ...rest }) => {
  const { close } = useContext(RowActionsContext);
  return (
    <button
      type="button"
      className={`dropdown-item ${className}`.trim()}
      onClick={event => {
        event.stopPropagation();
        close();
        if (onClick) onClick(event);
      }}
      {...rest}
    >
      {children}
    </button>
  );
};

const Divider = () => <div className="dropdown-divider" />;

RowActions.Item = Item;
RowActions.Divider = Divider;

export default RowActions;
